import { useState, useEffect, useRef } from "react";
import TransactionLog from "../components/transactionLog";
import CurrentOrdersList from "../components/currentOrdersList";
import calculateAllAmounts from "../helpers/getTotalAmount";
import getCurrentOrdersWithAnnotations from "../helpers/getCurrentOrdersWithAnnotations";
import { IconButton, Tooltip, Typography, Box } from "@mui/material";
import { Link } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import getHelper from "../helpers/getHelper";
import configs from '../helpers/configs';

export default function Viewer() {
  const [initData, setInitData] = useState([]);
  const initDataRef = useRef([]);
  const previousAmountRef = useRef(null);
  const [logData, setLogData] = useState({items:[]});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wakeLock, setWakeLock] = useState(null);
  const [totalAmount, setTotalAmount] = useState({
    color: "black",
    diff: 0,
    amount: 0
  });
  const [diffState, setDiffState] = useState(0);
  const [marketState, setMarketState] = useState(null);
  const { baseUrl } = configs;
  
  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  useEffect(() => {
    async function orders() {
      await getDeals();
      await getLog();
      await getTotalAmount();
    }
    orders();
  }, []);

  useEffect(() => {
    let interval;
    let countdownTimer;
  
    if (autoRefresh) {
      setCountdown(refreshInterval);
  
      interval = setInterval(() => {
        (async () => {
          await getDeals();
          await getLog();
          await getTotalAmount();
          await marketStateCalc();
          setCountdown(refreshInterval);
        })();
      }, refreshInterval * 1000); // convert seconds to ms
  
      countdownTimer = setInterval(() => {
        setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
  
    return () => {
      clearInterval(interval);
      clearInterval(countdownTimer);
    }
  }, [autoRefresh, refreshInterval]);

  const getLog = async () => {
    // here
    const log = await getHelper(`${baseUrl}/getlogs?amount=150`);

    const newLogData = {
      items: log.data.data.items.filter(item => item.action !== "sell"),
      next: log.data.next
    }
    setLogData(newLogData);
  }

  const marketStateCalc = async () => { 
    // Calculate market state based on the data
    // take the percentage and divide by the number of orders
    const data = initDataRef.current;
    const totalOrders = data.length;
    if (totalOrders === 0) return 0;
    const totalPercentage = data.reduce((acc, order) => acc + order.percentageDiff, 0); 
    setMarketState(totalPercentage / totalOrders);
  }

  const getTotalAmount = async () => {
    const returnObject = {
      color: "black",
      diff: diffState,
      amount: 0
    }
    const totalAmount = await calculateAllAmounts(initDataRef.current);
    const diff = totalAmount - previousAmountRef.current;
    if(diff < 0){
      returnObject.color = "red";
    } else if(diff > 0){
      returnObject.color = "green";
    }
    if(diff !== 0){
      returnObject.diff = diff.toFixed(2);
      setDiffState(diff.toFixed(2));
    }
    returnObject.amount = totalAmount;
    previousAmountRef.current = totalAmount;
    setTotalAmount(returnObject);
  }

  const getDeals = async () => {
    try {
      const newData = await getCurrentOrdersWithAnnotations(initDataRef);
      const { newData: annotatedData, updateTime } = newData;
      setInitData(annotatedData);
      setLastUpdated(updateTime);
      
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }; 

  const toggleRefresh = () => {
    setAutoRefresh(!autoRefresh);
  }

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setWakeLock(await navigator.wakeLock.request("screen"));
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      await wakeLock.release()
      setWakeLock(null);
      setIsFullscreen(false);
    }
  };

  return (
    <div id="viewer">
      <div id="viewerHeader" style={{ display: "flex", justifyContent: "space-between", padding: "1rem" }}>
        <div>
          <IconButton component={Link} to="/" color="primary">
            <HomeIcon />
          </IconButton>
        </div>

        <Box display="flex" alignItems="center" color="primary.main">
          <ReceiptIcon />
          <Typography variant="body1" sx={{ mr: 0.5 }}>
            {initData.length}
          </Typography>
        </Box>

      <Box display="flex" alignItems="center">
        <AccountBalanceWalletIcon />
        <Typography
          variant="body1"
          sx={{
            mr: 0.5,
            color: 'primary.main',
          }}
        >
          ${totalAmount.amount} 
          {typeof totalAmount.diff === 'number' && totalAmount.diff !== 0 &&(
            <Typography
              component="span"
              sx={{
                ml: 0.5,
                color: totalAmount.color,
                fontSize: '0.875rem',
              }}
            >
              ({totalAmount.diff >= 0 ? '+' : ''}{totalAmount.diff})
            </Typography>
          )}
        </Typography>
      </Box>

        <div>
          <IconButton onClick={toggleRefresh} color="primary">
            {autoRefresh ? <PauseIcon /> : <PlayArrowIcon />}
            {autoRefresh && <Typography variant="body1" sx={{ mr: 0.5 }}>{countdown < 10 ? `0${countdown}` : countdown}</Typography>}
          </IconButton>
        </div>
        <Tooltip title="Toggle Fullscreen">
          <IconButton onClick={toggleFullscreen} color="primary">
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </div>
      <div className='viewerItem orders'>
        {initData.length > 0 && 
          <CurrentOrdersList data={initData} component={'viewer'}/>
        }
      </div>
      <div className='viewerItem logs'>
        {/* {console.log('logData', logData)} */}
        <TransactionLog data={logData.items} itemClass='viewerLog'/>
      </div>
    </div>
  );
}
